/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CourseHeader
 *
 * @file CourseHeader.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { CourseHeaderContainer } from './styles/CourseHeaderStyles';
import CourseCard from './CourseCard';
import EtextCard from './EtextCard';
import UnifiedCourseCard from './UnifiedCourseCard';

const CourseHeader = (props) => {
  const {
    course = {},
    book = {},
    config = {},
    callback = {},
    onInit = () => {}
  } = props;

  const {
    showCourse = false,
    showBook = false,
    showClassSchedule = false,
    showGoToPage = false,
    showCourseActions = false,
    showBookActions = false,
    showInstructor = true,
    showCourseTitle = true,
    showBookTitle = true,
    showAuthor = true,
    showBookCover = true,
    showSccessEducatorGuides = true,
    showTitleInfo = true,
    showDownload = false,
    showInTwoColumns = false,
    showUnifiedColumn = false
  } = config;

  useEffect(() => {
    onInit();
  }, []);


  const renderBook = () => {
    if (!showBook) {
      return null;
    }

    return (
      <EtextCard
        book={book}
        config={{
          showGoToPage,
          showBookActions,
          showBookTitle,
          showAuthor,
          showBookCover,
          showSccessEducatorGuides,
          showTitleInfo,
          showDownload
        }}
      />
    );
  };

  const renderCourse = () => {
    if (!showCourse) {
      return null;
    }

    const renderChildren = showInTwoColumns ? null : renderBook();

    return (
      <CourseCard
        course={course}
        config={{
          showClassSchedule,
          showCourseActions,
          showInstructor,
          showCourseTitle,
          ...config
        }}
        callback={callback}
      >
        {renderChildren}
      </CourseCard>
    );
  };

  const renderUnifiedColumn = () => {
    if (!showUnifiedColumn || (!showCourse && !showBook)) {
      return null;
    }

    return (
      <UnifiedCourseCard
        course={course}
        book={book}
        config={config}
        callback={callback}
      />
    );
  };

  const renderContainer = () => {
    if (showUnifiedColumn) {
      return (
        <CourseHeaderContainer>
          {renderUnifiedColumn()}
        </CourseHeaderContainer>
      );
    }

    if (showInTwoColumns) {
      return (
        <CourseHeaderContainer twoColumns>
          {renderCourse()}
          {renderBook()}
        </CourseHeaderContainer>
      );
    }

    return (
      <CourseHeaderContainer>
        {renderCourse()}
      </CourseHeaderContainer>
    );
  };

  return renderContainer();
};

CourseHeader.propTypes = {
  course: PropTypes.object,
  book: PropTypes.object,
  config: PropTypes.object,
  callback: PropTypes.object,
  onInit: PropTypes.func
};

CourseHeader.defaultProps = {
  course: {},
  book: {},
  config: {},
  callback: {},
  onInit: () => {}
};

export default CourseHeader;
